import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { FileText, Plus, Trash2, Eye, Send, Award, Building, Calendar, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Curriculum = () => {
  const { canManageCurriculum, user } = useAuth();
  const canManage = canManageCurriculum();
  const [curriculums, setCurriculums] = useState([]);
  const [concorsiAperti, setConcorsiAperti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [inviaDialogOpen, setInviaDialogOpen] = useState(false);
  const [selectedCV, setSelectedCV] = useState(null);
  const [selectedConcorso, setSelectedConcorso] = useState(null);
  const [letteraMotivazionale, setLetteraMotivazionale] = useState('');
  const [submittingLDL, setSubmittingLDL] = useState(false);
  const [formData, setFormData] = useState({
    nome_candidato: '',
    cognome_candidato: '',
    telegram: '',
    esperienza: '',
    competenze: '',
    note: ''
  });

  useEffect(() => {
    fetchCurriculums();
    fetchConcorsiAperti();
  }, []);

  const fetchCurriculums = async () => {
    try {
      const response = await axios.get(`${API_URL}/curriculum`);
      setCurriculums(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei curriculum');
    } finally {
      setLoading(false);
    }
  };

  const fetchConcorsiAperti = async () => {
    try {
      const response = await axios.get(`${API_URL}/concorsi`);
      // Filtra solo concorsi aperti con modalità GESTIONALE
      const apertiGestionale = response.data.filter(c => c.stato === 'APERTO' && c.modalita === 'GESTIONALE');
      setConcorsiAperti(apertiGestionale);
    } catch (error) {
      console.error('Errore caricamento concorsi:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/curriculum`, formData);
      toast.success('Curriculum inserito con successo');
      setIsDialogOpen(false);
      setFormData({ nome_candidato: '', cognome_candidato: '', telegram: '', esperienza: '', competenze: '', note: '' });
      fetchCurriculums();
    } catch (error) {
      toast.error('Errore nell\'inserimento');
    }
  };

  const handleStatusChange = async (id, stato) => {
    try {
      await axios.patch(`${API_URL}/curriculum/${id}/stato?stato=${stato}`);
      toast.success('Stato aggiornato');
      fetchCurriculums();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo curriculum?')) return;
    try {
      await axios.delete(`${API_URL}/curriculum/${id}`);
      toast.success('Curriculum eliminato');
      fetchCurriculums();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleInviaAConcorso = (cv) => {
    setSelectedCV(cv);
    setSelectedConcorso(null);
    setLetteraMotivazionale('');
    setInviaDialogOpen(true);
  };

  const handleSubmitLDL = async () => {
    if (!selectedConcorso || !letteraMotivazionale.trim()) {
      toast.error('Seleziona un concorso e scrivi la lettera motivazionale');
      return;
    }
    
    setSubmittingLDL(true);
    try {
      await axios.post(`${API_URL}/ldl`, {
        curriculum_id: selectedCV.id,
        concorso_id: selectedConcorso,
        lettera_motivazionale: letteraMotivazionale
      });
      toast.success('Candidatura inviata con successo!');
      setInviaDialogOpen(false);
      setSelectedCV(null);
      setSelectedConcorso(null);
      setLetteraMotivazionale('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della candidatura');
    } finally {
      setSubmittingLDL(false);
    }
  };

  const getStatusBadge = (stato) => {
    const variants = {
      'RICEVUTO': 'bg-blue-100 text-blue-800',
      'IN_REVISIONE': 'bg-amber-100 text-amber-800',
      'APPROVATO': 'bg-green-100 text-green-800',
      'RIFIUTATO': 'bg-red-100 text-red-800'
    };
    const labels = {
      'RICEVUTO': 'Ricevuto',
      'IN_REVISIONE': 'In Revisione',
      'APPROVATO': 'Approvato',
      'RIFIUTATO': 'Rifiutato'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 text-xs font-semibold rounded-full ${variants[stato]}`}>
        {labels[stato]}
      </span>
    );
  };

  return (
    <div data-testid="curriculum-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-header flex items-center gap-3">
            <FileText className="w-8 h-8" />
            Curriculum Vitae
          </h1>
          <p className="text-slate-600 mt-1">Gestione candidature e curriculum</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-cv-btn" className="bg-slate-900 hover:bg-slate-800 rounded-sm">
              <Plus className="w-4 h-4 mr-2" />
              Inserisci CV
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Inserisci Curriculum</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome</Label>
                  <Input
                    id="nome"
                    data-testid="cv-nome-input"
                    value={formData.nome_candidato}
                    onChange={(e) => setFormData({ ...formData, nome_candidato: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cognome">Cognome</Label>
                  <Input
                    id="cognome"
                    data-testid="cv-cognome-input"
                    value={formData.cognome_candidato}
                    onChange={(e) => setFormData({ ...formData, cognome_candidato: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="telegram">Telegram</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">@</span>
                  <Input
                    id="telegram"
                    data-testid="cv-telegram-input"
                    className="pl-8"
                    placeholder="username"
                    value={formData.telegram}
                    onChange={(e) => setFormData({ ...formData, telegram: e.target.value.replace('@', '') })}
                    required
                  />
                </div>
                <p className="text-xs text-slate-500">Inserisci il tuo username Telegram senza @</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="esperienza">Esperienza</Label>
                <Textarea
                  id="esperienza"
                  data-testid="cv-esperienza-input"
                  value={formData.esperienza}
                  onChange={(e) => setFormData({ ...formData, esperienza: e.target.value })}
                  placeholder="Descrivi le esperienze lavorative..."
                  rows={3}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="competenze">Competenze</Label>
                <Textarea
                  id="competenze"
                  data-testid="cv-competenze-input"
                  value={formData.competenze}
                  onChange={(e) => setFormData({ ...formData, competenze: e.target.value })}
                  placeholder="Elenca le competenze..."
                  rows={3}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="note">Note Aggiuntive</Label>
                <Textarea
                  id="note"
                  data-testid="cv-note-input"
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="Note opzionali..."
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Annulla
                </Button>
                <Button type="submit" data-testid="submit-cv-btn" className="bg-slate-900 hover:bg-slate-800">
                  Inserisci
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* CV Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
        </div>
      ) : curriculums.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-slate-500">Nessun curriculum inserito</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead>Candidato</TableHead>
                <TableHead>Telegram</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead>Data</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {curriculums.map((cv) => (
                <TableRow key={cv.id} data-testid={`cv-row-${cv.id}`}>
                  <TableCell className="font-medium">
                    {cv.nome_candidato} {cv.cognome_candidato}
                  </TableCell>
                  <TableCell>
                    {cv.telegram ? (
                      <a 
                        href={`https://t.me/${cv.telegram}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <Send className="w-3 h-3" /> @{cv.telegram}
                      </a>
                    ) : cv.email ? (
                      <span className="text-sm text-slate-600">{cv.email}</span>
                    ) : (
                      <span className="text-sm text-slate-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {canManage ? (
                      <Select
                        value={cv.stato}
                        onValueChange={(value) => handleStatusChange(cv.id, value)}
                      >
                        <SelectTrigger className="w-36" data-testid={`cv-status-${cv.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="RICEVUTO">Ricevuto</SelectItem>
                          <SelectItem value="IN_REVISIONE">In Revisione</SelectItem>
                          <SelectItem value="APPROVATO">Approvato</SelectItem>
                          <SelectItem value="RIFIUTATO">Rifiutato</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      getStatusBadge(cv.stato)
                    )}
                  </TableCell>
                  <TableCell className="text-slate-500">
                    {new Date(cv.created_at).toLocaleDateString('it-IT')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {/* Pulsante Invia a Concorso - solo per CV del cittadino */}
                      {concorsiAperti.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`invia-cv-concorso-${cv.id}`}
                          onClick={() => handleInviaAConcorso(cv)}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <Award className="w-4 h-4 mr-1" />
                          Invia
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        data-testid={`view-cv-${cv.id}`}
                        onClick={() => { setSelectedCV(cv); setViewDialogOpen(true); }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-cv-${cv.id}`}
                          onClick={() => handleDelete(cv.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* View CV Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Dettaglio Curriculum</DialogTitle>
          </DialogHeader>
          {selectedCV && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Nome</Label>
                  <p className="font-medium">{selectedCV.nome_candidato}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Cognome</Label>
                  <p className="font-medium">{selectedCV.cognome_candidato}</p>
                </div>
              </div>
              <div>
                <Label className="text-slate-500">Contatto</Label>
                {selectedCV.telegram ? (
                  <a 
                    href={`https://t.me/${selectedCV.telegram}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <Send className="w-4 h-4" /> @{selectedCV.telegram}
                  </a>
                ) : (
                  <div className="space-y-1">
                    {selectedCV.email && <p className="font-medium">{selectedCV.email}</p>}
                    {selectedCV.telefono && <p className="text-slate-600">{selectedCV.telefono}</p>}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-slate-500">Esperienza</Label>
                <p className="mt-1 whitespace-pre-wrap">{selectedCV.esperienza}</p>
              </div>
              <div>
                <Label className="text-slate-500">Competenze</Label>
                <p className="mt-1 whitespace-pre-wrap">{selectedCV.competenze}</p>
              </div>
              {selectedCV.note && (
                <div>
                  <Label className="text-slate-500">Note</Label>
                  <p className="mt-1 whitespace-pre-wrap">{selectedCV.note}</p>
                </div>
              )}
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-500">Stato: {getStatusBadge(selectedCV.stato)}</span>
                  <span className="text-sm text-slate-500">
                    Inserito il {new Date(selectedCV.created_at).toLocaleDateString('it-IT')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Invia CV a Concorso */}
      <Dialog open={inviaDialogOpen} onOpenChange={setInviaDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl flex items-center gap-2">
              <Award className="w-5 h-5 text-green-600" />
              Invia Candidatura
            </DialogTitle>
            <DialogDescription>
              Seleziona un concorso e scrivi una lettera motivazionale per inviare la tua candidatura.
            </DialogDescription>
          </DialogHeader>
          {selectedCV && (
            <div className="space-y-4 mt-4">
              {/* Info CV selezionato */}
              <div className="bg-slate-50 p-3 rounded-lg border">
                <p className="text-sm text-slate-500">Curriculum di:</p>
                <p className="font-semibold">{selectedCV.nome_candidato} {selectedCV.cognome_candidato}</p>
              </div>

              {/* Selezione Concorso */}
              <div className="space-y-2">
                <Label>Seleziona Concorso *</Label>
                {concorsiAperti.length === 0 ? (
                  <p className="text-sm text-slate-500">Nessun concorso disponibile al momento</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {concorsiAperti.map((concorso) => (
                      <div
                        key={concorso.id}
                        onClick={() => setSelectedConcorso(concorso.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedConcorso === concorso.id
                            ? 'border-green-500 bg-green-50 ring-2 ring-green-200'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium">{concorso.titolo}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                              {concorso.azienda_nome && (
                                <span className="flex items-center gap-1">
                                  <Building className="w-3 h-3" />
                                  {concorso.azienda_nome}
                                </span>
                              )}
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Scade: {new Date(concorso.scadenza).toLocaleDateString('it-IT')}
                              </span>
                            </div>
                          </div>
                          {selectedConcorso === concorso.id && (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Lettera Motivazionale */}
              <div className="space-y-2">
                <Label htmlFor="lettera">Lettera Motivazionale *</Label>
                <Textarea
                  id="lettera"
                  value={letteraMotivazionale}
                  onChange={(e) => setLetteraMotivazionale(e.target.value)}
                  placeholder="Spiega perché sei il candidato ideale per questa posizione..."
                  rows={5}
                  className="resize-none"
                />
              </div>

              {/* Azioni */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setInviaDialogOpen(false)}
                  disabled={submittingLDL}
                >
                  Annulla
                </Button>
                <Button 
                  onClick={handleSubmitLDL}
                  disabled={!selectedConcorso || !letteraMotivazionale.trim() || submittingLDL}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {submittingLDL ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Invia Candidatura
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Curriculum;
